import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from './Modal'
import PostDetailModal from './PostDetailModal'
import contentService from '../services/contentService'
import {
  HITOS,
  permisosAprobacion,
  fmtFecha,
  diasRestantes,
  textoDias,
  fetchAprobacionesPendientes,
} from '../utils/aprobaciones'
import './VencidosModal.css' // clases .vm-* compartidas (toolbar, tabla, toast…)
import './AprobacionesModal.css'

const PAGE_SIZE = 25
const hoyStr = () => new Date().toLocaleDateString('en-CA') // YYYY-MM-DD en hora local

const snoozeKey = (user) => `geves_aprobaciones_modal_${user?.id ?? user?.username ?? 'anon'}`
const getSnooze = (user) => { try { return localStorage.getItem(snoozeKey(user)) } catch { return null } }
const setSnooze = (user) => { try { localStorage.setItem(snoozeKey(user), hoyStr()) } catch { /* sin storage: se mostrará de nuevo */ } }

const HITOS_ORDEN = ['idea_creativa', 'copy', 'contenido']

/**
 * Modal de aprobaciones pendientes para el Calendario.
 *  - Paso 1 (alert): resumen con el total y botón "Ver detalle".
 *  - Paso 2 (detail): listado con aprobar/rechazar idea creativa y copy directamente
 *    (según los grupos del usuario). Los archivos de contenido se revisan abriendo el post.
 * Misma fuente de datos que /aprobaciones. Se muestra como máximo una vez al día por usuario.
 */
export default function AprobacionesModal({ user, enabled = true, ready = true, onChanged, onDone }) {
  const navigate = useNavigate()
  const [step, setStep] = useState('closed') // 'closed' | 'alert' | 'detail'
  const [posts, setPosts] = useState([])
  const [q, setQ] = useState('')
  const [hito, setHito] = useState('')
  const [soloMios, setSoloMios] = useState(false)
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [expandidos, setExpandidos] = useState(new Set())
  const [procesando, setProcesando] = useState(null) // `${postId}-${hito}`
  const [postModalId, setPostModalId] = useState(null)
  const [toast, setToast] = useState(null)
  const changedRef = useRef(false)
  const startedRef = useRef(false)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone // "terminó" = no hay nada que mostrar o ya se cerró (pasa el turno al siguiente aviso)

  const permisos = useMemo(() => permisosAprobacion(user), [user])
  const esAprobador = permisos.idea_creativa || permisos.copy

  // ── Carga inicial (una sola vez, cuando es su turno): solo si no se descartó hoy ──
  useEffect(() => {
    if (!ready || startedRef.current) return
    startedRef.current = true
    if (!enabled || getSnooze(user) === hoyStr()) {
      if (onDoneRef.current) onDoneRef.current()
      return
    }
    fetchAprobacionesPendientes()
      .then(list => {
        if (list.length === 0) { if (onDoneRef.current) onDoneRef.current(); return }
        setPosts(list)
        setStep('alert')
      })
      .catch(() => { if (onDoneRef.current) onDoneRef.current() }) // silencioso: /aprobaciones sigue disponible
  }, [ready, enabled, user])

  const close = useCallback(() => {
    setSnooze(user)
    setStep('closed')
    setToast(null)
    if (changedRef.current) {
      changedRef.current = false
      if (onChanged) onChanged()
    }
    if (onDoneRef.current) onDoneRef.current()
  }, [user, onChanged])

  useEffect(() => {
    if (step === 'closed' || postModalId) return
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [step, postModalId, close])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(t)
  }, [toast])

  // ── Derivados ────────────────────────────────────────────────────────────
  const conteos = useMemo(() => {
    const c = { idea_creativa: 0, copy: 0, contenido: 0 }
    posts.forEach(p => p.pendientes.forEach(h => { if (c[h] !== undefined) c[h] += 1 }))
    return c
  }, [posts])

  const aprobables = useMemo(
    () => posts.filter(p => p.pendientes.some(h => permisos[h])).length,
    [posts, permisos]
  )

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    return posts.filter(p =>
      (!hito || p.pendientes.includes(hito)) &&
      (!soloMios || p.pendientes.some(h => permisos[h])) &&
      (!t ||
        (p.titulo || '').toLowerCase().includes(t) ||
        (p.cliente || '').toLowerCase().includes(t) ||
        (p.influencer || '').toLowerCase().includes(t) ||
        (p.plataforma || '').toLowerCase().includes(t))
    )
  }, [posts, q, hito, soloMios, permisos])

  useEffect(() => { setVisible(PAGE_SIZE) }, [q, hito, soloMios])

  const shown = filtered.slice(0, visible)

  // ── Acciones ─────────────────────────────────────────────────────────────
  const recargar = async () => {
    try { setPosts(await fetchAprobacionesPendientes()) } catch { /* se mantiene la lista actual */ }
  }

  const actuar = async (post, h, accion) => {
    if (procesando) return
    let notas = ''
    if (accion === 'rejected') {
      const r = window.prompt('Notas de rechazo (opcional):')
      if (r === null) return
      notas = r
    }
    setProcesando(`${post.id}-${h}`)
    try {
      const res = h === 'idea_creativa'
        ? await contentService.approveCreativeIdea(post.id, accion, notas)
        : await contentService.approveCopy(post.id, accion, notas)
      changedRef.current = true
      setToast({
        type: 'success',
        message: res?.data?.message || (accion === 'approved' ? 'Aprobado' : 'Rechazado'),
      })
      await recargar() // el backend decide qué hitos quedan pendientes
    } catch (e) {
      setToast({
        type: 'error',
        message: e.response?.data?.error || e.response?.data?.message || 'Error al procesar la aprobación',
      })
    } finally {
      setProcesando(null)
    }
  }

  const toggleExpandido = (key) => setExpandidos(prev => {
    const n = new Set(prev)
    n.has(key) ? n.delete(key) : n.add(key)
    return n
  })

  if (step === 'closed') return null

  const total = posts.length
  const plural = total === 1 ? '' : 's'

  return (
    <>
      {/* ── Modal 1: aviso ── */}
      <Modal
        isOpen={step === 'alert'}
        onClose={close}
        minimal
        size="sm"
        title={`Tienes ${total} post${plural} con aprobaciones pendientes`}
        footer={(
          <>
            <button className="g-btn g-btn-secondary" onClick={close}>Ahora no</button>
            <button className="g-btn g-btn-primary" onClick={() => setStep('detail')}>Ver detalle</button>
          </>
        )}
      >
        <p className="vm-lead">
          Existe{total === 1 ? '' : 'n'} <strong>{total} post{plural}</strong> con al menos un hito
          sin aprobar (idea creativa, copy o contenido).
          {esAprobador && (
            <> De ellos, <strong>{aprobables}</strong> tiene{aprobables === 1 ? '' : 'n'} algo que tú puedes aprobar.</>
          )}
        </p>
        <div className="vm-breakdown">
          <span className="vm-breakdown-title">Pendiente de aprobación</span>
          <div className="vm-chips">
            {HITOS_ORDEN.map(h => (
              <span key={h} className="vm-chip">
                <i className={`bi ${HITOS[h].icon}`} style={{ color: HITOS[h].color }} /> {HITOS[h].label} <strong>{conteos[h]}</strong>
              </span>
            ))}
          </div>
        </div>
      </Modal>

      {/* ── Modal 2: detalle ── */}
      <Modal
        isOpen={step === 'detail' && !postModalId}
        onClose={close}
        minimal
        size="xl"
        title={`Aprobaciones pendientes · ${total}`}
        subtitle="Aprueba o rechaza idea creativa y copy aquí mismo. Los archivos de contenido se revisan abriendo el post."
        footerAlignment="between"
        footer={(
          <>
            <span className="vm-foot-info">
              Mostrando {shown.length} de {filtered.length}
              {filtered.length !== total ? ` (${total} en total)` : ''} · más urgentes primero
            </span>
            <div className="vm-foot-actions">
              <button className="g-btn g-btn-secondary" onClick={() => { close(); navigate('/aprobaciones') }}>
                Ir a Aprobaciones
              </button>
              <button className="g-btn g-btn-primary" onClick={close}>Cerrar</button>
            </div>
          </>
        )}
      >
        <div className="vm-toolbar">
          <input
            className="vm-input"
            type="search"
            placeholder="Buscar por título, cliente, cuenta o plataforma…"
            aria-label="Buscar post"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <select className="vm-select" aria-label="Filtrar por hito" value={hito} onChange={e => setHito(e.target.value)}>
            <option value="">Todos los hitos</option>
            {HITOS_ORDEN.map(h => <option key={h} value={h}>{HITOS[h].label} ({conteos[h]})</option>)}
          </select>
          {esAprobador && (
            <label className="am-check">
              <input type="checkbox" checked={soloMios} onChange={e => setSoloMios(e.target.checked)} />
              Solo lo que puedo aprobar ({aprobables})
            </label>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="g-empty">
            <i className="bi bi-check2-circle g-empty-icon" />
            <p className="g-empty-title">{total === 0 ? 'Sin aprobaciones pendientes' : 'Ningún post coincide con los filtros'}</p>
          </div>
        ) : (
          <div className="vm-scroll">
            <table className="g-table vm-table">
              <thead>
                <tr>
                  <th>Post</th>
                  <th>Plataforma</th>
                  <th>Publicación</th>
                  <th>Pendiente de aprobación</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map(p => {
                  const dias = diasRestantes(p.fecha_publicacion)
                  const diasClass = dias == null ? '' : dias < 0 ? 'am-dias-late' : dias <= 2 ? 'am-dias-soon' : 'am-dias-ok'
                  return (
                    <tr key={p.id}>
                      <td style={{ maxWidth: 240, verticalAlign: 'top' }}>
                        <div className="am-post-title">{p.titulo}</div>
                        <div className="am-sub">
                          {[p.influencer, p.cliente, p.tipo_material].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'top' }}>{p.plataforma || '—'}</td>
                      <td className="am-fecha" style={{ verticalAlign: 'top' }}>
                        {fmtFecha(p.fecha_publicacion)}
                        <div className={`am-dias ${diasClass}`}>{textoDias(dias)}</div>
                      </td>
                      <td className="am-cell-hitos">
                        {HITOS_ORDEN.filter(h => p.pendientes.includes(h)).map(h => {
                          const info = HITOS[h]
                          const key = `${p.id}-${h}`
                          const esTexto = h !== 'contenido'
                          const texto = h === 'idea_creativa' ? p.idea_creativa_texto : h === 'copy' ? p.copy_texto : null
                          const abierto = expandidos.has(key)
                          const busy = procesando === key
                          return (
                            <div className="am-hito" key={h}>
                              <div className="am-hito-head">
                                <span className="am-chip" style={{ color: info.color, background: info.bg }}>
                                  <i className={`bi ${info.icon}`} /> {info.label}
                                </span>
                                {!esTexto && p.media_total > 0 && (
                                  <span className="am-meta">{p.media_aprobados}/{p.media_total} archivos</span>
                                )}
                                {esTexto && texto && permisos[h] && (
                                  <span className="am-actions">
                                    <button className="g-btn g-btn-primary g-btn-sm" disabled={!!procesando} onClick={() => actuar(p, h, 'approved')}>
                                      {busy ? '…' : <><i className="bi bi-check2" /> Aprobar</>}
                                    </button>
                                    <button className="g-btn g-btn-secondary g-btn-sm" disabled={!!procesando} onClick={() => actuar(p, h, 'rejected')}>
                                      <i className="bi bi-x" /> Rechazar
                                    </button>
                                  </span>
                                )}
                                {esTexto && texto && !permisos[h] && (
                                  <span className="am-meta" style={{ marginLeft: 'auto' }}>Solo aprobadores</span>
                                )}
                                {!esTexto && (
                                  <span className="am-actions">
                                    <button className="g-btn g-btn-secondary g-btn-sm" onClick={() => setPostModalId(p.id)}>
                                      Revisar archivos
                                    </button>
                                  </span>
                                )}
                              </div>
                              {esTexto && (
                                <>
                                  <p className={`am-texto ${abierto ? 'am-texto-open' : ''} ${texto ? '' : 'am-texto-empty'}`}>
                                    {texto || 'Sin contenido registrado todavía.'}
                                  </p>
                                  {texto && texto.length > 140 && (
                                    <button className="am-link" onClick={() => toggleExpandido(key)}>
                                      {abierto ? 'Ver menos' : 'Ver completo'}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )
                        })}
                      </td>
                      <td style={{ verticalAlign: 'top' }}>
                        <button className="g-btn g-btn-ghost g-btn-sm" onClick={() => setPostModalId(p.id)}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {filtered.length > visible && (
              <div className="vm-more">
                <button className="g-btn g-btn-secondary" onClick={() => setVisible(v => v + PAGE_SIZE)}>
                  Cargar {Math.min(PAGE_SIZE, filtered.length - visible)} más
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Detalle completo del post (mismo componente que usa /aprobaciones) */}
      {postModalId && (
        <PostDetailModal
          postId={postModalId}
          onClose={() => { setPostModalId(null); changedRef.current = true; recargar() }}
          onUpdate={() => { changedRef.current = true; recargar() }}
        />
      )}

      {toast && (
        <div className={`vm-toast vm-toast-${toast.type}`} role="status">
          <span>{toast.message}</span>
          <button className="vm-toast-btn vm-toast-x" aria-label="Cerrar aviso" onClick={() => setToast(null)}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}
    </>
  )
}
